import { useEffect, useRef, type ElementType, type ReactNode } from "react";
import { cn } from "@/lib/utils";

/**
 * Scroll-triggered fade-up reveal (DESIGN.md motion system).
 * Adds `.in` when the element enters the viewport; animates once.
 * `delay` maps to the stagger classes reveal-d1..d3.
 */
export function Reveal({
  as: Tag = "div",
  delay,
  className,
  children,
}: {
  as?: ElementType;
  delay?: 1 | 2 | 3;
  className?: string;
  children: ReactNode;
}) {
  const ref = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("in");
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.18 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <Tag ref={ref} className={cn("reveal", delay && `reveal-d${delay}`, className)}>
      {children}
    </Tag>
  );
}
